<script lang="ts">
  import {onMount, onDestroy, createEventDispatcher} from 'svelte';
  import {flip} from 'svelte/animate';
  import {quartInOut} from 'svelte/easing';
  import InlineSVG from 'svelte-inline-svg';

  import type {Project, ProjectDraft} from '../../types/project';

  import {user} from '../../stores/user';

  import checkIcon from '../../assets/icons/check.svg';

  import ProjectCard from '../project-card/ProjectCard.svelte';
  import {Language, selectedLanguage} from '../../stores/language';

  export let projects: Array<Project | ProjectDraft>;
  export let selectedProjectIds: Array<string>;

  enum ProjectsListMediaQueries {
    MEDIA_XS = '(max-width: 599px)',
    MEDIA_S = '(min-width: 600px) and (max-width: 899px)',
    MEDIA_M = '(min-width: 900px) and (max-width: 1199px)',
    MEDIA_L = '(min-width: 1200px) and (max-width: 1499px)',
    MEDIA_XL = '(min-width: 1500px)'
  }

  const mediaQueryLists = [
    window.matchMedia(ProjectsListMediaQueries.MEDIA_XS),
    window.matchMedia(ProjectsListMediaQueries.MEDIA_S),
    window.matchMedia(ProjectsListMediaQueries.MEDIA_M),
    window.matchMedia(ProjectsListMediaQueries.MEDIA_L),
    window.matchMedia(ProjectsListMediaQueries.MEDIA_XL)
  ];

  const projectsPerRowByMedia: Record<string, number> = {
    [ProjectsListMediaQueries.MEDIA_XS]: 1,
    [ProjectsListMediaQueries.MEDIA_S]: 2,
    [ProjectsListMediaQueries.MEDIA_M]: 3,
    [ProjectsListMediaQueries.MEDIA_L]: 4,
    [ProjectsListMediaQueries.MEDIA_XL]: 5
  };

  let projectsPerChunk = 0;
  let loadedChunks = 1;
  let listEndElement: HTMLDivElement;

  $: isEnglishSelected = $selectedLanguage === Language.EN;

  $: projectsToDisplay = projects.slice(0, loadedChunks * projectsPerChunk);

  // Sets the chunk size to two rows of projects depending on the screen size
  const updateChunkSize = (
    mediaQueryList: MediaQueryList | MediaQueryListEvent
  ) => {
    if (mediaQueryList.matches) {
      projectsPerChunk = 2 * projectsPerRowByMedia[mediaQueryList.media];
    }
  };

  // Display more projects when scrolled to the end of the list
  const displayMoreProjects = () => {
    const allProjectsDisplayed = projects.length <= projectsToDisplay.length;

    // Stop listening when all projects are loaded
    if (allProjectsDisplayed) {
      return;
    }

    const listEndElementRect = listEndElement.getBoundingClientRect();
    const endOfListIsReached = listEndElementRect.bottom <= window.innerHeight;

    if (endOfListIsReached) {
      loadedChunks++;
    }
  };

  onMount(() => {
    // Update chunk size depending on the screen size
    mediaQueryLists.forEach(mediaQueryList => {
      updateChunkSize(mediaQueryList);
      mediaQueryList.addEventListener('change', updateChunkSize);
    });

    // Handle lazy loading
    document.addEventListener('wheel', displayMoreProjects);
  });

  onDestroy(() => {
    mediaQueryLists.forEach(mediaQueryList => {
      mediaQueryList.removeEventListener('change', updateChunkSize);
    });

    document.removeEventListener('wheel', displayMoreProjects);
  });

  const dispatch = createEventDispatcher();
</script>

<style lang="postcss">
  .projectsGrid {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    gap: 1rem;
  }

  @media (min-width: 600px) {
    .projectsGrid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (min-width: 900px) {
    .projectsGrid {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }
  @media (min-width: 1200px) {
    .projectsGrid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }
  @media (min-width: 1500px) {
    .projectsGrid {
      grid-template-columns: repeat(5, minmax(0, 1fr));
    }
  }

  .projectContainer {
    position: relative;
  }

  .selected {
    border-radius: var(--ubilabs-border-radius-medium);
    background-color: rgba(var(--ubilabs-purple-light-3-rgb), 0.5);
  }

  .project {
    height: 100%;
    transition: all 100ms ease-in;
  }

  .selected > .project {
    transform: scale(0.9);
  }

  .selectable > .project {
    pointer-events: none;
  }

  .check {
    position: absolute;
    top: 0;
    right: 0;
    width: 1.5rem;
    height: 1.5rem;
    margin: 0.5rem;
    padding: 0.125rem;
    border-radius: 50%;
    background-color: var(--ubilabs-white);
    box-shadow: 0 0 1px rgba(var(--ubilabs-black-rgb), 0.4),
      0 0 14px rgba(var(--ubilabs-black-rgb), 0.2);
    color: var(--ubilabs-gray-dark-1);
    font-size: 1.25rem;
  }

  @media (min-width: 768px) {
    .check {
      display: none;
    }
  }

  .selected > .check {
    display: block;
    background-color: var(--ubilabs-purple);
    color: var(--ubilabs-white);
  }

  .projectContainer:not(.selected):hover > .check,
  .selectable > .check {
    display: block;
  }

  .projectContainer:not(.selected):hover > .check:hover,
  .selectable:not(.selected):hover > .check {
    color: var(--ubilabs-gray-dark-2);
  }
</style>

<div class="projectsGrid">
  {#each projectsToDisplay as project (project.id)}
    <div
      class="projectContainer"
      class:selected={selectedProjectIds.includes(project.id)}
      class:selectable={Boolean(selectedProjectIds.length)}
      on:click={() =>
        selectedProjectIds.length && dispatch('toggle', project.id)}
      animate:flip={{duration: 400, easing: quartInOut, delay: 100}}>
      <div class="project">
        <ProjectCard
          id={project.id}
          type={project.type}
          isDraft={project.isDraft}
          title={isEnglishSelected
            ? project.title_En || project.title
            : project.title}
          confidentiality={project.confidentiality}
          customers={project.customers}
          description={isEnglishSelected
            ? project.description_En || project.description
            : project.description}
          media={project.media}
          creationPeriod={project.creationPeriod} />
      </div>

      {#if $user}
        <div
          class="check"
          on:click|stopPropagation={() => dispatch('toggle', project.id)}>
          <InlineSVG src={checkIcon} />
        </div>
      {/if}
    </div>
  {/each}

  <div
    hidden={projects.length <= projectsToDisplay.length}
    bind:this={listEndElement} />
</div>
